import { useFormValidation } from '../../../../hooks/useFormValidation'
import React, { useEffect, useRef } from 'react'
import { useDebouncedValue } from '../../../../hooks/useDebouncedValue'
import { useAppDispatch } from '../../../../hooks/useAppDispatch'
import { buildControlsExt, selectInput } from '../../../inputs/controls'
import { getLabel } from '../../../../utils/getLabel'
import {
    modifyActionParameter,
    setActionValid,
} from '../../../../store/actions/actionsActions'
import { blockConstructor } from '../../../models/Block/control'
import style from '../../../models/Block/Styles/index.module.scss'
import { Collapse } from 'react-bootstrap'
import { useExpand } from '../../../../hooks/useExpand'
import { generateUniqueLocalId } from '../../utils/generateUniqueLocalId'
import { generateAriaId } from '../../utils/generateAriaId'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { selectActionParams } from '../../../../store/selectors/actionsSelectors/actionsParametersSelectors'
import { DescriptionBtnWithPopup } from '../../../buttons/PromptBtn/DescriptionBtnWithPopup'
import { PATH_JOINER } from '../../../models/utils/getConfigBitPath'

const MODE = 'mode'
const PERIOD = 'period'
const FROM_LAST_ACTIVITY = 'from_last_activity'
const FROM = 'from'
const TO = 'to'
const STORAGE_KEY_FROM = 'actions_sync_settings_period_from'
const STORAGE_KEY_TO = 'actions_sync_settings_period_to'

interface SyncSettingsProps {
    dataPath: string
    data: any
    dataSchema: any
    name: string
    wrapper?: string
    levelDeep?: number
}

export const SyncSettings = (props: SyncSettingsProps) => {
    const dispatch = useAppDispatch()
    const {
        dataPath,
        data = {},
        dataSchema = {},
        name,
        wrapper,
        levelDeep = 2,
    } = props

    const blockPath = [...(dataPath.split(PATH_JOINER)), name]
    const componentSchema = dataSchema.children

    const actionParams = useAppSelector(
        selectActionParams(blockPath[0], blockPath[1])
    )
    const periodFrom = actionParams?.[name]?.period?.from
    const periodTo = actionParams?.[name]?.period?.to

    const schemaKeys = Object.keys(componentSchema)
    const hasMode = schemaKeys.includes(MODE)
    const hasPeriod = schemaKeys.includes(PERIOD)
    const modeSchema = componentSchema.mode

    const periodSchema = componentSchema.period
    const optionKeys = useRef(Object.keys(modeSchema.options ?? {}))

    const [modeState, setModeState, modeTouched, setModeTouched] =
        useFormValidation(data?.[name]?.mode, {})
    const debouncedModeState = useDebouncedValue(modeState, 500)

    const controlDescriptors = [
        selectInput({
            name: MODE,
            options: optionKeys.current,
            selected:
                data?.[name]?.mode ||
                modeSchema?.default ||
                optionKeys.current[0],
            tooltipText: modeSchema.description,
            label: getLabel(MODE, modeSchema.name),
            className: !wrapper ? 'w-100 pe-2' : '',
            isColumn: true,
        }),
    ]

    const EnumInput = buildControlsExt(
        controlDescriptors,
        modeState,
        setModeState,
        modeTouched,
        setModeTouched,
    )

    useEffect(() => {
        dispatch(
            modifyActionParameter({
                path: [...blockPath, MODE],
                data: modeState,
            })
        )
    }, [debouncedModeState]) // eslint-disable-line

    useEffect(() => {
        if (modeState?.mode === FROM_LAST_ACTIVITY) {
            if (periodFrom) {
                sessionStorage.setItem(
                    `${blockPath[0]} ${STORAGE_KEY_FROM}`,
                    String(periodFrom)
                )
            }
            if (periodTo) {
                sessionStorage.setItem(
                    `${blockPath[0]} ${STORAGE_KEY_TO}`,
                    String(periodTo)
                )
            }
            dispatch(
                modifyActionParameter({
                    path: [...blockPath, PERIOD],
                    data: { period: null },
                })
            )
            dispatch(setActionValid({ path: [...blockPath, PERIOD, FROM] }))
            dispatch(setActionValid({ path: [...blockPath, PERIOD, TO] }))
        }
    }, [debouncedModeState]) // eslint-disable-line

    useEffect(() => {
        if (modeState?.mode === PERIOD) {
            const from =
                sessionStorage.getItem(`${blockPath[0]} ${STORAGE_KEY_FROM}`) ??
                null

            const to =
                sessionStorage.getItem(`${blockPath[0]} ${STORAGE_KEY_TO}`) ??
                null

            dispatch(
                modifyActionParameter({
                    path: [...blockPath, PERIOD],
                    data: {
                        period: {
                            from: periodFrom ?? from,
                            to: periodTo ?? to,
                        },
                    },
                })
            )
        }
    }, [debouncedModeState]) // eslint-disable-line

    useEffect(() => {
        return () => {
            sessionStorage.removeItem(`${blockPath[0]} ${STORAGE_KEY_FROM}`)
            sessionStorage.removeItem(`${blockPath[0]} ${STORAGE_KEY_TO}`)
        }
    }, []) // no need to add blockPath. It changes on every render

    const ariaID = generateAriaId()
    const { expandBtn, expanded, toggle } = useExpand({
        isExpanded: !periodSchema?.is_auxiliary,
        uniqueID: generateUniqueLocalId({
            path: [...blockPath, PERIOD],
            name: PERIOD,
        }),
        ariaID,
        className: style.button,
    })

    const desc = !!periodSchema?.description && (
        <DescriptionBtnWithPopup
            type={'small'}
            header={name}
            description={periodSchema?.description}
            className="ms-auto me-2 text-white"
        />
    )

    const block = !data?.[name]?.period
        ? null
        : blockConstructor({
              dataSchema: periodSchema,
              data: data?.[name]?.period,
              dataProp: data || {},
              name: PERIOD,
              childrenPath: [...blockPath, PERIOD].join(PATH_JOINER),
              dataPath: blockPath.join(PATH_JOINER),
              pathWithElementID: undefined,
              blockState: true,
              builderType: 'actionUiBuilder',
              levelDeep: 2,
              element_id: 'element_id',
              isRuleActive: true,
          })

    const styleHeader = `${style.header} block-header-${levelDeep} block-bg-header-default p-0 rounded-top`

    if (!hasMode || !hasPeriod) {
        return <>Wrong Sync Settings Schema</>
    }
    return (
        <>
            <div className={`${!wrapper ? 'w-100' : ''}`}>{EnumInput}</div>
            {modeState?.mode === PERIOD && (
                <div
                    className={`w-100 border-left border-right border-bottom mw-350 d-flex flex-column rounded border mt-2 mb-2 position-relative me-4 align-self-start`}
                >
                    <div className={styleHeader}>
                        <div
                            className={!wrapper ? 'w-100' : ''}
                            onClick={toggle}
                        >
                            <h4 className={style.title}>
                                {wrapper === 'block_dictionary_card' ? (
                                    <span style={{ opacity: 0 }}>{name}</span>
                                ) : (
                                    getLabel(PERIOD, periodSchema?.name)
                                )}
                            </h4>
                        </div>
                        {desc}
                        {expandBtn}
                    </div>
                    <Collapse in={expanded}>
                        <div className="p-relative">
                            <div
                                className="p-3 app-bg-primary rounded-bottom"
                                id={ariaID}
                            >
                                {block}
                            </div>
                        </div>
                    </Collapse>
                </div>
            )}
        </>
    )
}